import { useState } from "react";
import { Button, Card, Icon, Space, Table, Tag, Text, Tooltip } from "@app/ui";
import { exportPassportVersion, listPassportVersions, verifyPassportChain } from "../api/dpp.js";
import { useAsync } from "../api/useAsync.js";
import { useLang, useT, useFormatDateTime } from "../i18n.js";
import { PassportJsonModal } from "./PassportJsonModal.js";
import type { ChainVerification, PassportDetail } from "../types.js";

/**
 * The signed version chain: list, integrity check, official exports and the
 * in-panel JSON-LD viewer. Loads its own versions, re-fetching whenever the
 * passport's current version moves (i.e. after a publish).
 */
export function PassportVersionsCard({
  orgId,
  passport,
  onError,
}: {
  orgId: string;
  passport: PassportDetail;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const { lang } = useLang();
  const fmtDate = useFormatDateTime();
  const { data } = useAsync(
    () => listPassportVersions(orgId, passport.id),
    [orgId, passport.id, passport.currentVersionNo],
  );
  const versions = data?.items ?? [];
  const [verification, setVerification] = useState<ChainVerification | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [exporting, setExporting] = useState<number | null>(null);
  // Version whose JSON-LD dump is open in the viewer; null = closed.
  const [viewingJson, setViewingJson] = useState<number | null>(null);

  const verify = async () => {
    setVerifying(true);
    setVerification(null);
    try {
      setVerification(await verifyPassportChain(orgId, passport.id));
    } catch (e) {
      onError(e);
    } finally {
      setVerifying(false);
    }
  };

  const exportVersion = async (versionNo: number, format: "html" | "jsonld") => {
    setExporting(versionNo);
    try {
      const blob = await exportPassportVersion(
        orgId,
        passport.id,
        versionNo,
        lang === "tr" ? "tr" : "en",
        format,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `passport-${passport.publicSlug}-v${versionNo}.${format === "html" ? "html" : "jsonld"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(e);
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <Card
        title={t("passport.versions")}
        extra={
          <Space size="sm">
            {verification &&
              (verification.valid ? (
                <Tag color="success">{t("passport.chainValid")}</Tag>
              ) : (
                <Tag color="error">
                  {t("passport.chainBroken")} v{verification.firstBrokenAt}
                </Tag>
              ))}
            <Button
              size="sm"
              loading={verifying}
              disabled={versions.length === 0}
              icon={<Icon name="shield" size={14} />}
              onClick={() => void verify()}
            >
              {t("passport.verify")}
            </Button>
          </Space>
        }
      >
        <Table
          rowKey={(v) => String(v.versionNo)}
          empty={t("passport.noVersions")}
          columns={[
            {
              key: "versionNo",
              title: t("passports.version"),
              width: 130,
              render: (v) => (
                <span className="flex items-center gap-2">
                  <span className="font-medium tabular-nums">v{v.versionNo}</span>
                  {v.versionNo === passport.currentVersionNo && (
                    <Tag color="primary">{t("passport.latest")}</Tag>
                  )}
                </span>
              ),
            },
            { key: "createdAt", title: t("common.createdAt"), render: (v) => fmtDate(v.createdAt) },
            {
              key: "hash",
              title: t("passport.hash"),
              render: (v) => (
                <Tooltip title={v.hash}>
                  <Text code copyable={{ text: v.hash }}>
                    {v.hash.slice(0, 12)}…
                  </Text>
                </Tooltip>
              ),
            },
            {
              key: "compliance",
              title: t("product.compliance"),
              width: 110,
              render: (v) => `${v.complianceScore}%`,
              numeric: true,
            },
            {
              key: "export",
              title: t("passport.exportOfficial"),
              render: (v) => (
                <Space size="sm">
                  <Tooltip title={t("passport.exportHint")}>
                    <Space size="sm">
                      <Button
                        size="sm"
                        variant="subtle"
                        icon={<Icon name="download" size={14} />}
                        loading={exporting === v.versionNo}
                        onClick={() => void exportVersion(v.versionNo, "html")}
                      >
                        HTML
                      </Button>
                      <Button
                        size="sm"
                        variant="subtle"
                        icon={<Icon name="download" size={14} />}
                        loading={exporting === v.versionNo}
                        onClick={() => void exportVersion(v.versionNo, "jsonld")}
                      >
                        JSON-LD
                      </Button>
                    </Space>
                  </Tooltip>
                  {/* Reads the same audited export endpoint, rendered in-panel instead of saved.
                      Icon-only: a labelled third button squeezes the date and hash columns into wrapping. */}
                  <Tooltip title={t("passport.viewJson")}>
                    <Button
                      size="sm"
                      variant="subtle"
                      aria-label={t("passport.viewJson")}
                      icon={<Icon name="eye" size={14} />}
                      onClick={() => setViewingJson(v.versionNo)}
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          data={versions}
        />
        {versions.length > 0 && (
          <Text small secondary className="mt-3 block">
            {t("passport.versionsHint")}
          </Text>
        )}
      </Card>
      <PassportJsonModal
        orgId={orgId}
        passportId={passport.id}
        publicSlug={passport.publicSlug}
        versionNo={viewingJson}
        onClose={() => setViewingJson(null)}
      />
    </>
  );
}
